import { useEffect } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'

// Directional Cone Component for Leaflet Maps
const DirectionalCone = ({ 
  position, 
  direction, 
  coneLength = 100, // in meters
  coneWidth = 30,   // in degrees
  opacity = 0.6,
  color = '#3B82F6' // blue color
}) => {
  const map = useMap()

  useEffect(() => {
    if (!position || !direction || !map) return

    const [lat, lng] = position
    
    // Create cone points
    const conePoints = createConePoints(lat, lng, direction, coneLength, coneWidth)
    
    // Create the cone polygon
    const cone = L.polygon(conePoints, {
      color: color,
      weight: 1,
      opacity: 0.8,
      fillOpacity: opacity,
      fillColor: color
    })

    // Add gradient effect by creating multiple overlapping polygons
    const gradientCones = createGradientCones(lat, lng, direction, coneLength, coneWidth, color, opacity)
    
    // Add all cones to map
    gradientCones.forEach(cone => map.addLayer(cone))
    map.addLayer(cone)

    // Cleanup function
    return () => {
      gradientCones.forEach(cone => map.removeLayer(cone))
      map.removeLayer(cone)
    }
  }, [position, direction, coneLength, coneWidth, opacity, color, map])

  return null
}

// Helper function to create cone points
const createConePoints = (lat, lng, direction, length, width) => {
  const points = []
  
  // Convert length from meters to degrees (approximate)
  const lengthInDegrees = length / 111000 // rough conversion
  
  // Calculate the tip of the cone
  const tipLat = lat + lengthInDegrees * Math.cos((direction - 90) * Math.PI / 180)
  const tipLng = lng + lengthInDegrees * Math.sin((direction - 90) * Math.PI / 180)
  
  // Calculate the base points of the cone
  const halfWidth = width / 2
  const leftDirection = direction - halfWidth
  const rightDirection = direction + halfWidth
  
  const leftLat = lat + lengthInDegrees * 0.3 * Math.cos((leftDirection - 90) * Math.PI / 180)
  const leftLng = lng + lengthInDegrees * 0.3 * Math.sin((leftDirection - 90) * Math.PI / 180)
  
  const rightLat = lat + lengthInDegrees * 0.3 * Math.cos((rightDirection - 90) * Math.PI / 180)
  const rightLng = lng + lengthInDegrees * 0.3 * Math.sin((rightDirection - 90) * Math.PI / 180)
  
  // Create the cone shape
  points.push([lat, lng]) // center point
  points.push([leftLat, leftLng]) // left base
  points.push([tipLat, tipLng]) // tip
  points.push([rightLat, rightLng]) // right base
  points.push([lat, lng]) // back to center
  
  return points
}

// Helper function to create gradient effect with multiple overlapping cones
const createGradientCones = (lat, lng, direction, length, width, color, opacity) => {
  const cones = []
  const steps = 5 // number of gradient steps
  
  for (let i = 0; i < steps; i++) {
    const stepLength = length * (i + 1) / steps
    const stepOpacity = (opacity * (steps - i)) / steps
    const stepWidth = width * (1 - i * 0.1) // slightly narrower for each step
    
    const conePoints = createConePoints(lat, lng, direction, stepLength, stepWidth)
    
    const cone = L.polygon(conePoints, {
      color: color,
      weight: 0,
      opacity: 0,
      fillOpacity: stepOpacity,
      fillColor: color
    })
    
    cones.push(cone)
  }
  
  return cones
}

export default DirectionalCone

